import React from "react";
import { ReactComponent as InternetIcon } from "@assets/Icons/IconInternet.svg";
import { ReactComponent as PortIcon } from "@assets/Icons/IconServer.svg";
import { ReactComponent as CloudIcon } from "@assets/Icons/IconConnectivityKontrol.svg";
import { ReactComponent as DatacenterIcon } from "@assets/Icons/IconDataInterconnect.svg";
import { ReactComponent as FwaasIcon } from "@assets/Icons/IconFwaas.svg";
import { ReactComponent as SdwanIcon } from "@assets/Icons/Icon_sdwan.svg";
import { KindHealth, ServiceHealth, ServiceKindKey, ServiceScope, worstHealth } from "./overviewModel";

/** The SideMenu's icon for each kind, so a chip and its menu entry look alike. */
const KIND_ICON: Record<ServiceKindKey, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  ports: PortIcon,
  internet: InternetIcon,
  cloudConnectivity: CloudIcon,
  datacenterInterconnect: DatacenterIcon,
  fwaas: FwaasIcon,
  sdwan: SdwanIcon,
};

export const HEALTH_DOT: Record<ServiceHealth, string> = {
  up: "bg-semantic-fg-success",
  warning: "bg-semantic-fg-warning",
  down: "bg-semantic-fg-danger",
  unknown: "bg-semantic-fg-secondary",
};

const HEALTH_LABEL: Record<ServiceHealth, string> = {
  up: "all up",
  warning: "degraded",
  down: "down",
  unknown: "no data",
};

/** What a chip flags: how many need a look, red when any is down, else orange. */
export interface ChipIssues {
  count: number;
  isCritical: boolean;
}

const ISSUE_BADGE = {
  critical: "bg-semantic-bg-danger-secondary text-semantic-text-danger",
  warning: "bg-semantic-bg-warning-secondary text-semantic-text-warning",
};

function kindIssues(kind: KindHealth): ChipIssues {
  return { count: kind.down + kind.warning, isCritical: kind.down > 0 };
}

/** "3 down", "1 degraded", "all up" — the state a chip's tooltip reads out. */
function describeKind(kind: KindHealth): string {
  if (kind.down > 0) return `${kind.down} down`;
  if (kind.warning > 0) return `${kind.warning} degraded`;
  return HEALTH_LABEL[worstHealth(kind)];
}

function Chip({
  selected,
  onClick,
  icon,
  label,
  count,
  issues,
  title,
}: {
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: string;
  count: number;
  issues: ChipIssues;
  title: string;
}) {
  const tone = selected
    ? "border-semantic-border-brand-primary bg-semantic-bg-brand-secondary text-semantic-text-brand"
    : "border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-secondary hover:bg-semantic-hover-primary";
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={title}
      onClick={onClick}
      className={`inline-flex h-8 shrink-0 items-center gap-2 rounded-full border px-3 text-xs font-semibold transition-colors ${tone}`}
    >
      {icon}
      <span>{label}</span>
      <span className="tabular-nums text-semantic-text-primary">{count}</span>
      {/* Only trouble is marked: a green dot on every healthy chip says nothing. */}
      {issues.count > 0 && (
        <span
          className={`-mr-1.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
            ISSUE_BADGE[issues.isCritical ? "critical" : "warning"]
          }`}
        >
          {issues.count}
        </span>
      )}
    </button>
  );
}

/**
 * One chip per service kind with its count and, when something is wrong, how
 * many need a look; plus "All services". Kinds the tenant does not have are
 * left out: a row of zeros is noise.
 */
export function ServiceScopeChips({
  kinds,
  openIssues,
  scope,
  onScopeChange,
}: {
  kinds: KindHealth[];
  /**
   * Everything open across kinds, ongoing outages included — the list's own
   * count, so "All services" and the list header never disagree.
   */
  openIssues: ChipIssues;
  scope: ServiceScope;
  onScopeChange: (scope: ServiceScope) => void;
}) {
  const present = kinds.filter((kind) => kind.total > 0);
  // Ports are infrastructure, not services: "All services" counts services only.
  // Their issues still count in its badge.
  const serviceCount = present
    .filter((kind) => kind.key !== "ports")
    .reduce((sum, kind) => sum + kind.total, 0);
  const totals = present.reduce(
    (acc, kind) => ({
      total: acc.total + kind.total,
      up: acc.up + kind.up,
      warning: acc.warning + kind.warning,
      down: acc.down + kind.down,
    }),
    { total: 0, up: 0, warning: 0, down: 0 }
  );

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by service type">
      <Chip
        selected={scope === "all"}
        onClick={() => onScopeChange("all")}
        label="All services"
        count={serviceCount}
        issues={openIssues}
        title={`All services: ${describeKind({ key: "ports", label: "", unknown: 0, ...totals })}`}
      />
      {present.map((kind) => {
        const Icon = KIND_ICON[kind.key];
        return (
          <Chip
            key={kind.key}
            selected={scope === kind.key}
            onClick={() => onScopeChange(kind.key)}
            icon={<Icon className="h-4 w-4" aria-hidden />}
            label={kind.label}
            count={kind.total}
            issues={kindIssues(kind)}
            title={`${kind.label}: ${describeKind(kind)}`}
          />
        );
      })}
    </div>
  );
}
